import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useAuthSync } from '../../hooks/useAuthSync';
import ProfileService from '../../services/profileService';
import BillingService from '../../services/billingService';
import OrganizationService from '../../services/organizationService';
import Button from '../common/Button';
import '../profile/Profile.css'; // Use Profile styles for consistency

const BillingConfig = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { getAppRole } = useAuthSync();
  const appRole = getAppRole();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [defaultSubmittingId, setDefaultSubmittingId] = useState(null);
  const [hasFiscalInfo, setHasFiscalInfo] = useState(false);
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(false);
  const [files, setFiles] = useState({
    cerFile: null,
    keyFile: null
  });
  const [password, setPassword] = useState('');
  const [setDefaultOnUpload, setSetDefaultOnUpload] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [userData, setUserData] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [defaultCertificateId, setDefaultCertificateId] = useState('');
  const [billingSerie, setBillingSerie] = useState('');
  const [billingNextFolio, setBillingNextFolio] = useState('');
  const [billingEmitterName, setBillingEmitterName] = useState('');
  const [timezone, setTimezone] = useState('America/Mexico_City');
  const [activeTab, setActiveTab] = useState('certificates');

  const isAdminOrOwner = useMemo(() => {
    const normalized = (appRole || '').toLowerCase();
    return ['admin', 'owner'].includes(normalized);
  }, [appRole]);

  useEffect(() => {
    const checkFiscalInfo = async () => {
      try {
        if (!auth.user) return;
        
        const userId = auth.user.profile.sub;
        const token = auth.user.access_token;
        
        const profile = await ProfileService.getUserProfile(userId, token);
        setUserData(profile);

        if (profile.organizationId) {
          try {
            const org = await OrganizationService.getOrganizationById(profile.organizationId, token);
            setOrganization(org);
            setCertificates(Array.isArray(org?.billingCertificates) ? org.billingCertificates : []);
            setDefaultCertificateId(org?.billingDefaultCertificateId || '');
            setBillingSerie(org?.billingSerie || '');
            setBillingNextFolio(Number.isInteger(org?.billingNextFolio) ? String(org.billingNextFolio) : '1');
            setBillingEmitterName(org?.billingEmitterName || '');
            setTimezone(org?.timezone || 'America/Mexico_City');
            setSetDefaultOnUpload(!org?.billingDefaultCertificateId);

            if (org && org.tags && org.tags.includes('fe_enabled')) {
              setIsFeatureEnabled(true);
            } else {
              setIsFeatureEnabled(false);
            }
          } catch (orgError) {
            console.error('Error fetching organization details:', orgError);
            setIsFeatureEnabled(false);
          }
        } else {
          setIsFeatureEnabled(false);
        }
        
        if (profile.rfc && profile.taxRegime && profile.zipCode) {
          setHasFiscalInfo(true);
        } else {
          setHasFiscalInfo(false);
        }
      } catch (error) {
        setMessage({ type: 'error', text: 'Error al cargar información del usuario' });
      } finally {
        setLoading(false);
      }
    };
    
    checkFiscalInfo();
  }, [auth.user]);

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({
        ...prev,
        [type]: file
      }));
    }
  };

  // Helper to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove data:application/x-x509-ca-cert;base64, part
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!files.cerFile || !files.keyFile || !password) {
      setMessage({ type: 'error', text: 'Por favor complete todos los campos' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });
    
    try {
      const b64Cer = await fileToBase64(files.cerFile);
      const b64Key = await fileToBase64(files.keyFile);
      
      await BillingService.saveCertificates({
        b64Cer,
        b64Key,
        password,
        setDefault: setDefaultOnUpload
      }, auth.user.access_token);
      
      setMessage({ type: 'success', text: 'Certificados guardados exitosamente' });

      if (organization?.id) {
        const updatedOrg = await OrganizationService.getOrganizationById(organization.id, auth.user.access_token);
        setOrganization(updatedOrg);
        setCertificates(Array.isArray(updatedOrg?.billingCertificates) ? updatedOrg.billingCertificates : []);
        setDefaultCertificateId(updatedOrg?.billingDefaultCertificateId || '');
        setBillingSerie(updatedOrg?.billingSerie || '');
        setBillingNextFolio(Number.isInteger(updatedOrg?.billingNextFolio) ? String(updatedOrg.billingNextFolio) : '1');
        setBillingEmitterName(updatedOrg?.billingEmitterName || '');
        setTimezone(updatedOrg?.timezone || 'America/Mexico_City');
        setSetDefaultOnUpload(!updatedOrg?.billingDefaultCertificateId);
      }

      // Reset form
      setFiles({ cerFile: null, keyFile: null });
      setPassword('');
      // Clear file inputs
      document.getElementById('cerFile').value = '';
      document.getElementById('keyFile').value = '';
      
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Error al guardar certificados' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetDefault = async (certificateId) => {
    if (!certificateId || defaultSubmittingId) return;

    try {
      setDefaultSubmittingId(certificateId);
      setMessage({ type: '', text: '' });
      await BillingService.setDefaultCertificate(certificateId, auth.user.access_token);
      setDefaultCertificateId(certificateId);
      setMessage({ type: 'success', text: 'Certificado predeterminado actualizado' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Error al actualizar el certificado predeterminado' });
    } finally {
      setDefaultSubmittingId(null);
    }
  };

  const handleSaveBillingConfig = async (e) => {
    e.preventDefault();
    if (!organization?.id) return;

    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });
      const token = auth.user?.access_token;
      const nextFolioValue = billingNextFolio === '' ? '' : Number(billingNextFolio);
      const updatedOrg = await OrganizationService.updateOrganization(organization.id, {
        billingSerie: billingSerie.trim(),
        billingNextFolio: Number.isInteger(nextFolioValue) ? nextFolioValue : undefined,
        billingEmitterName: billingEmitterName.trim()
      }, token);
      setOrganization(updatedOrg);
      setBillingSerie(updatedOrg?.billingSerie || '');
      setBillingNextFolio(Number.isInteger(updatedOrg?.billingNextFolio) ? String(updatedOrg.billingNextFolio) : '');
      setMessage({ type: 'success', text: 'Configuración actualizada correctamente' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Error al actualizar la configuración' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleDateString('es-MX', { timeZone: timezone });
  };

  const formatDateTime = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleString('es-MX', { timeZone: timezone });
  };

  const sortedCertificates = useMemo(() => {
    return [...certificates].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
  }, [certificates]);

  const defaultCertificate = useMemo(() => {
    return certificates.find(cert => cert.id === defaultCertificateId) || null;
  }, [certificates, defaultCertificateId]);

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  if (!isFeatureEnabled) {
    return (
      <div className="profile-container">
        <div className="profile-header">
           <h1>Configuración de Facturación</h1>
           <p>Gestiona tus certificados digitales para facturación</p>
        </div>
        
        <div className="profile-content">
          <div className="profile-card">
            <div className="alert alert-info" style={{ 
              backgroundColor: '#ebf8ff', 
              color: '#2c5282', 
              padding: '2rem', 
              borderRadius: '8px', 
              border: '1px solid #bee3f8',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '1.2rem' }}><i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i> Funcionalidad no disponible</h4>
              <p style={{ margin: '5px 0' }}>La facturación electrónica no está habilitada para tu organización actualmente.</p>
              <p style={{ margin: '5px 0' }}>Por favor contacta a soporte (soporte@digitaport.com) para solicitar la activación de este módulo.</p>
              <Button 
                variant="secondary" 
                onClick={() => navigate('/dashboard')}
                style={{ marginTop: '20px' }}
              >
                Volver al Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasFiscalInfo) {
    return (
      <div className="profile-container">
        <div className="profile-header">
           <h1>Configuración de Facturación</h1>
           <p>Gestiona tus certificados digitales para facturación</p>
        </div>
        
        <div className="profile-content">
          <div className="profile-card">
            <div className="alert alert-warning" style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '1rem', borderRadius: '4px', border: '1px solid #ffeeba' }}>
              <h4 style={{ marginTop: 0, marginBottom: '10px' }}><i className="fas fa-exclamation-triangle"></i> Información Fiscal Requerida</h4>
              <p>Para configurar la facturación, primero debes completar tu Información Fiscal en tu perfil.</p>
              <p>Campos faltantes: 
                 {userData && !userData.rfc ? ' RFC ' : ''}
                 {userData && !userData.taxRegime ? ' Régimen Fiscal ' : ''}
                 {userData && !userData.zipCode ? ' Código Postal' : ''}
              </p>
              <Button 
                variant="primary" 
                onClick={() => navigate('/settings/profile')}
                style={{ marginTop: '15px' }}
              >
                Ir a Mi Perfil
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Configuración de Facturación</h1>
        <p>Gestiona los certificados de tu organización para emitir facturas</p>
      </div>

      <div className="profile-content">
        <div className="profile-card">
          
          {defaultCertificate ? (
            <div className="current-cert-info" style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
               <h3 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', color: '#2d3748' }}>Certificado Predeterminado</h3>
               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div style={{ wordBreak: 'break-all' }}>
                    <span style={{ display: 'block', fontSize: '0.85rem', color: '#718096', marginBottom: '4px' }}>Número de Certificado</span>
                    <span style={{ fontSize: '1rem', fontWeight: '500', color: '#2d3748' }}>{defaultCertificate.serialNumber || 'N/A'}</span>
                  </div>
                  <div style={{ wordBreak: 'break-all' }}>
                    <span style={{ display: 'block', fontSize: '0.85rem', color: '#718096', marginBottom: '4px' }}>RFC</span>
                    <span style={{ fontSize: '1rem', fontWeight: '500', color: '#2d3748' }}>{organization?.fiscalId || defaultCertificate.rfc || 'N/A'}</span>
                  </div>
                  <div>
                    <span style={{ display: 'block', fontSize: '0.85rem', color: '#718096', marginBottom: '4px' }}>Vigencia</span>
                    <span style={{ fontSize: '1rem', fontWeight: '500', color: '#2d3748' }}>{formatDate(defaultCertificate.validTo)}</span>
                  </div>
                  <div>
                    <span style={{ display: 'block', fontSize: '0.85rem', color: '#718096', marginBottom: '4px' }}>Última Actualización</span>
                    <span style={{ fontSize: '1rem', fontWeight: '500', color: '#2d3748' }}>{formatDateTime(defaultCertificate.uploadedAt)}</span>
                  </div>
               </div>
            </div>
          ) : (
            <div className="alert alert-info" style={{ marginBottom: '25px', backgroundColor: '#ebf8ff', color: '#2c5282', padding: '1rem', borderRadius: '4px' }}>
              <i className="fas fa-info-circle"></i> No has subido certificados aún. Sube tus archivos .cer y .key para habilitar la facturación.
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <button
              type="button"
              onClick={() => setActiveTab('certificates')}
              style={{
                padding: '10px 16px',
                borderRadius: '999px',
                border: activeTab === 'certificates' ? '1px solid #2563eb' : '1px solid #e2e8f0',
                backgroundColor: activeTab === 'certificates' ? '#2563eb' : '#ffffff',
                color: activeTab === 'certificates' ? '#ffffff' : '#1f2937',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Certificados
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('serie')}
              style={{
                padding: '10px 16px',
                borderRadius: '999px',
                border: activeTab === 'serie' ? '1px solid #2563eb' : '1px solid #e2e8f0',
                backgroundColor: activeTab === 'serie' ? '#2563eb' : '#ffffff',
                color: activeTab === 'serie' ? '#ffffff' : '#1f2937',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Serie y Folio
            </button>
          </div>

          {activeTab === 'certificates' ? (
            <>
              {isAdminOrOwner ? (
                <form onSubmit={handleSubmit} className="profile-form">
                  <div className="form-section">
                    <h3>Subir Certificado (CSD)</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cerFile">Certificado (.cer)</label>
                    <input
                      type="file"
                      id="cerFile"
                      accept=".cer"
                      onChange={(e) => handleFileChange(e, 'cerFile')}
                      className="file-input"
                      style={{ padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px', width: '100%' }}
                    />
                    {files.cerFile && <small style={{display: 'block', marginTop: '5px', color: '#2ecc71'}}>{files.cerFile.name}</small>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="keyFile">Llave Privada (.key)</label>
                    <input
                      type="file"
                      id="keyFile"
                      accept=".key"
                      onChange={(e) => handleFileChange(e, 'keyFile')}
                      className="file-input"
                      style={{ padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px', width: '100%' }}
                    />
                    {files.keyFile && <small style={{display: 'block', marginTop: '5px', color: '#2ecc71'}}>{files.keyFile.name}</small>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="password">Contraseña de la llave privada</label>
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="********"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      id="setDefault"
                      checked={setDefaultOnUpload}
                      onChange={(e) => setSetDefaultOnUpload(e.target.checked)}
                      disabled={!defaultCertificateId}
                    />
                    <label htmlFor="setDefault" style={{ margin: 0 }}>
                      Establecer como predeterminado
                    </label>
                  </div>
                </div>
              </div>

                  {message.text && (
                    <div className={`message ${message.type}`}>
                      {message.text}
                    </div>
                  )}

                  <div className="form-actions">
                    <Button
                      type="submit"
                      variant="primary"
                      loading={submitting}
                      disabled={submitting}
                      icon="fas fa-save"
                    >
                      Guardar Certificados
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="alert alert-info" style={{ marginBottom: '20px', backgroundColor: '#ebf8ff', color: '#2c5282', padding: '1rem', borderRadius: '4px' }}>
                  <i className="fas fa-info-circle"></i> Solo administradores y propietarios pueden subir certificados.
                </div>
              )}

              {sortedCertificates.length > 0 && (
                <div style={{ marginTop: '25px' }}>
                  <h3 style={{ marginBottom: '15px', fontSize: '1.05rem', color: '#2d3748' }}>Historial de Certificados</h3>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {sortedCertificates.map(cert => (
                      <div
                        key={cert.id}
                        style={{
                          padding: '15px',
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          gap: '12px',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ display: 'grid', gap: '6px' }}>
                          <div style={{ fontWeight: '600', color: '#2d3748', wordBreak: 'break-all' }}>
                            Serie: {cert.serialNumber || 'N/A'}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                            Vigencia: {formatDate(cert.validTo)}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                            Subido: {formatDateTime(cert.uploadedAt)}
                          </div>
                          {cert.uploadedBy?.name && (
                            <div style={{ fontSize: '0.85rem', color: '#718096' }}>
                              Por: {cert.uploadedBy.name}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                          {cert.id === defaultCertificateId ? (
                            <span style={{
                              backgroundColor: '#10b981',
                              color: 'white',
                              fontSize: '0.75rem',
                              padding: '4px 10px',
                              borderRadius: '999px'
                            }}>
                              Predeterminado
                            </span>
                          ) : (
                            isAdminOrOwner && (
                              <Button
                                variant="secondary"
                                onClick={() => handleSetDefault(cert.id)}
                                loading={defaultSubmittingId === cert.id}
                                disabled={defaultSubmittingId === cert.id}
                              >
                                Hacer predeterminado
                              </Button>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <form onSubmit={handleSaveBillingConfig} className="profile-form">
              <div className="form-section">
                <h3>Serie y Folio</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="billingEmitterName">Nombre del Emisor</label>
                    <input
                      type="text"
                      id="billingEmitterName"
                      value={billingEmitterName}
                      onChange={(e) => setBillingEmitterName(e.target.value)}
                      placeholder="Razón social del emisor"
                      maxLength="200"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="billingSerie">Serie</label>
                    <input
                      type="text"
                      id="billingSerie"
                      value={billingSerie}
                      onChange={(e) => setBillingSerie(e.target.value)}
                      placeholder="Dejar vacío para no usar serie"
                      maxLength="25"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="billingNextFolio">Próximo Folio</label>
                    <input
                      type="number"
                      id="billingNextFolio"
                      min="1"
                      value={billingNextFolio}
                      onChange={(e) => setBillingNextFolio(e.target.value)}
                      placeholder="Ej. 1"
                    />
                  </div>
                </div>
                <small style={{ display: 'block', marginTop: '5px', color: '#718096' }}>
                  Si se configura, se usará automáticamente al generar facturas.
                </small>
              </div>
              {message.text && (
                <div className={`message ${message.type}`}>
                  {message.text}
                </div>
              )}
              <div className="form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting}
                >
                  {submitting ? <span className="loading-spinner"></span> : 'Guardar configuración'}
                </Button>
              </div>
            </form>
          )}
        </div>

        <div className="profile-security">
          <h3>Información Importante</h3>
          <div className="security-item">
            <div className="security-info">
              <h4>CSD vs FIEL</h4>
              <p>Asegúrate de subir los archivos de tu <strong>Certificado de Sello Digital (CSD)</strong> y no los de tu e.firma (FIEL).</p>
              <p style={{ marginTop: '10px', fontSize: '0.85rem' }}>La e.firma sirve para trámites en el SAT, mientras que el CSD es exclusivo para firmar facturas.</p>
            </div>
          </div>
          
          <div className="security-item">
            <div className="security-info">
              <h4>Vigencia</h4>
              <p>Los certificados tienen una vigencia de 4 años.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingConfig;
